// src/store/fileManagment/reducer.ts

import {
  START_UPLOAD,
  UPDATE_UPLOAD_PROGRESS,
  UPDATE_UPLOAD_STATUS,
  COMPLETE_UPLOAD,
  FAIL_UPLOAD,
  CANCEL_UPLOAD,
  REMOVE_UPLOAD,
  RETRY_UPLOAD,
  PAUSE_UPLOAD,
  RESUME_UPLOAD,
  SET_UPLOAD_CHUNK_INFO,
  FileUploadState,
  FileUploadActionTypes,
  UploadState,
  UploadStatus,
  ChunkInfo,
  initialState
} from './types';

// Fonction utilitaire pour calculer la progression globale
const calculateGlobalProgress = (uploads: { [uploadId: string]: UploadState }): number => {
  const activeUploads = Object.values(uploads).filter(
    upload => [UploadStatus.UPLOADING, UploadStatus.PENDING, UploadStatus.RETRYING].includes(upload.status)
  );
  
  if (activeUploads.length === 0) return 0;
  
  const totalProgress = activeUploads.reduce((sum, upload) => sum + upload.progress, 0);
  return Math.round(totalProgress / activeUploads.length);
};

// Fonction utilitaire pour compter les uploads actifs
const countActiveUploads = (uploads: { [uploadId: string]: UploadState }): number => {
  return Object.values(uploads).filter(
    upload => [UploadStatus.UPLOADING, UploadStatus.PENDING, UploadStatus.RETRYING, UploadStatus.PAUSED].includes(upload.status)
  ).length;
};

// Fonction utilitaire pour initialiser les chunks
const initializeChunks = (totalChunks: number, chunkSize: number): ChunkInfo[] => {
  return Array.from({ length: totalChunks }, (_, index) => ({
    chunkIndex: index,
    totalChunks,
    chunkSize,
    uploaded: false,
    retryCount: 0
  }));
};

// Fonction utilitaire pour calculer les bytes uploadés basés sur les chunks
const calculateUploadedBytes = (chunks: ChunkInfo[], chunkSize: number, fileSize: number): number => {
  let uploadedBytes = 0;
  chunks.forEach((chunk, index) => {
    if (chunk.uploaded) {
      // Le dernier chunk peut être plus petit
      const isLastChunk = index === chunks.length - 1;
      const actualChunkSize = isLastChunk ? fileSize - (index * chunkSize) : chunkSize;
      uploadedBytes += actualChunkSize;
    }
  });
  return uploadedBytes;
};

const fileUploadReducer = (
  state = initialState, 
  action: FileUploadActionTypes
): FileUploadState => {
  switch (action.type) {
    case START_UPLOAD: {
      const { 
        id, 
        fileName, 
        fileSize, 
        fileType, 
        mimeType, 
        totalChunks, 
        chunkSize, 
        maxRetries = 3,
        metadata,
        entityId,
        entityType
      } = action.payload;

      const newUpload: UploadState = {
        id,
        fileName,
        fileSize,
        fileType,
        mimeType,
        status: UploadStatus.PENDING,
        progress: 0,
        uploadedBytes: 0,
        startTime: new Date(),
        retryCount: 0,
        maxRetries,
        chunks: initializeChunks(totalChunks, chunkSize),
        currentChunkIndex: 0,
        metadata,
        entityId,
        entityType
      };

      const newUploads = { ...state.uploads, [id]: newUpload };
      
      return {
        ...state,
        uploads: newUploads,
        activeUploadsCount: countActiveUploads(newUploads),
        totalUploadsCount: Object.keys(newUploads).length,
        globalProgress: calculateGlobalProgress(newUploads)
      };
    }

    case UPDATE_UPLOAD_PROGRESS: {
      const { id, progress, uploadedBytes, uploadSpeed, estimatedTimeRemaining } = action.payload;
      const upload = state.uploads[id];
      
      if (!upload) return state;

      const updatedUpload: UploadState = {
        ...upload,
        progress: Math.min(100, Math.max(0, progress)),
        uploadedBytes,
        uploadSpeed,
        estimatedTimeRemaining,
        status: upload.status === UploadStatus.PENDING ? UploadStatus.UPLOADING : upload.status
      };

      const newUploads = { ...state.uploads, [id]: updatedUpload };

      return {
        ...state,
        uploads: newUploads,
        globalProgress: calculateGlobalProgress(newUploads)
      };
    }

    case UPDATE_UPLOAD_STATUS: {
      const { id, status, error } = action.payload;
      const upload = state.uploads[id];
      
      if (!upload) return state;

      const updatedUpload: UploadState = {
        ...upload,
        status,
        error: error || upload.error
      };

      const newUploads = { ...state.uploads, [id]: updatedUpload };

      return {
        ...state,
        uploads: newUploads,
        activeUploadsCount: countActiveUploads(newUploads),
        globalProgress: calculateGlobalProgress(newUploads)
      };
    }

    case COMPLETE_UPLOAD: {
      const { id } = action.payload;
      const upload = state.uploads[id];
      
      if (!upload) return state;

      const updatedUpload: UploadState = {
        ...upload,
        status: UploadStatus.COMPLETED,
        progress: 100,
        endTime: new Date(),
        error: undefined
      };

      const newUploads = { ...state.uploads, [id]: updatedUpload };

      return {
        ...state,
        uploads: newUploads,
        activeUploadsCount: countActiveUploads(newUploads),
        globalProgress: calculateGlobalProgress(newUploads)
      };
    }

    case FAIL_UPLOAD: {
      const { id, error } = action.payload;
      const upload = state.uploads[id];
      
      if (!upload) return state;

      const updatedUpload: UploadState = {
        ...upload,
        status: UploadStatus.FAILED,
        error,
        endTime: new Date()
      };

      const newUploads = { ...state.uploads, [id]: updatedUpload };

      return {
        ...state,
        uploads: newUploads,
        activeUploadsCount: countActiveUploads(newUploads),
        globalProgress: calculateGlobalProgress(newUploads)
      };
    }

    case CANCEL_UPLOAD: {
      const { id } = action.payload;
      const upload = state.uploads[id];
      
      if (!upload) return state;

      const updatedUpload: UploadState = {
        ...upload,
        status: UploadStatus.CANCELLED,
        endTime: new Date()
      };

      const newUploads = { ...state.uploads, [id]: updatedUpload };

      return {
        ...state,
        uploads: newUploads,
        activeUploadsCount: countActiveUploads(newUploads),
        globalProgress: calculateGlobalProgress(newUploads)
      };
    }

    case REMOVE_UPLOAD: {
      const { id } = action.payload;
      const newUploads = { ...state.uploads };
      delete newUploads[id];

      return {
        ...state,
        uploads: newUploads,
        activeUploadsCount: countActiveUploads(newUploads),
        totalUploadsCount: Object.keys(newUploads).length,
        globalProgress: calculateGlobalProgress(newUploads)
      };
    }

    case RETRY_UPLOAD: {
      const { id } = action.payload;
      const upload = state.uploads[id];
      
      if (!upload) return state;

      const updatedUpload: UploadState = {
        ...upload,
        status: UploadStatus.RETRYING,
        retryCount: upload.retryCount + 1,
        error: undefined,
        progress: 0,
        uploadedBytes: 0,
        currentChunkIndex: 0,
        chunks: upload.chunks.map(chunk => ({ ...chunk, uploaded: false, retryCount: 0 }))
      };

      const newUploads = { ...state.uploads, [id]: updatedUpload };

      return {
        ...state,
        uploads: newUploads,
        activeUploadsCount: countActiveUploads(newUploads),
        globalProgress: calculateGlobalProgress(newUploads)
      };
    }

    case PAUSE_UPLOAD: {
      const { id } = action.payload;
      const upload = state.uploads[id];
      
      if (!upload || ![UploadStatus.UPLOADING, UploadStatus.RETRYING].includes(upload.status)) {
        return state;
      }

      const updatedUpload: UploadState = {
        ...upload,
        status: UploadStatus.PAUSED
      };

      const newUploads = { ...state.uploads, [id]: updatedUpload };

      return {
        ...state,
        uploads: newUploads,
        globalProgress: calculateGlobalProgress(newUploads)
      };
    }

    case RESUME_UPLOAD: {
      const { id } = action.payload;
      const upload = state.uploads[id];
      
      if (!upload || upload.status !== UploadStatus.PAUSED) {
        return state;
      }

      const updatedUpload: UploadState = {
        ...upload,
        status: UploadStatus.UPLOADING
      };

      const newUploads = { ...state.uploads, [id]: updatedUpload };

      return {
        ...state,
        uploads: newUploads,
        globalProgress: calculateGlobalProgress(newUploads)
      };
    }

    case SET_UPLOAD_CHUNK_INFO: {
      const { id, chunkIndex, uploaded, retryCount = 0 } = action.payload;
      const upload = state.uploads[id];
      
      if (!upload) return state;

      const updatedChunks = upload.chunks.map((chunk, index) =>
        index === chunkIndex 
          ? { ...chunk, uploaded, retryCount }
          : chunk
      );

      const uploadedBytes = calculateUploadedBytes(updatedChunks, updatedChunks[0]?.chunkSize || 0, upload.fileSize);
      const progress = Math.round((uploadedBytes / upload.fileSize) * 100);

      const updatedUpload: UploadState = {
        ...upload,
        chunks: updatedChunks,
        currentChunkIndex: uploaded ? Math.max(upload.currentChunkIndex, chunkIndex + 1) : upload.currentChunkIndex,
        uploadedBytes,
        progress
      };

      const newUploads = { ...state.uploads, [id]: updatedUpload };

      return {
        ...state,
        uploads: newUploads,
        globalProgress: calculateGlobalProgress(newUploads)
      };
    }

    default:
      return state;
  }
};

export default fileUploadReducer;